import {
  collection,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { firebase } from "./firebase-config";
import { COLLECTIONS } from "./config";

/** Human-readable labels for userActionLog.event_name (snake_case keys) */
export const USER_ACTION_EVENT_LABELS = {
  register: "Registered",
  login: "Login",
  create_employee: "Employee created",
  create_client: "Client created",
  payment: "Payment",
  create_issue: "Issue created",
  create_supply: "Supply request",
  create_support: "Support ticket",
};

/** Event names stored in userActionLog.event_name */
export const USER_ACTION_EVENTS = {
  REGISTER: "register",
  LOGIN: "login",
  CREATE_EMPLOYEE: "create_employee",
  CREATE_CLIENT: "create_client",
  PAYMENT: "payment",
  CREATE_ISSUE: "create_issue",
  CREATE_SUPPLY: "create_supply",
  CREATE_SUPPORT: "create_support",
};

/**
 * Persists an audit row to userActionLog and updates last activity on the target profile doc.
 * Call with void / fire-and-forget from UI; errors are logged only.
 *
 * @param {object} params
 * @param {string} params.userId - Acting user (auth uid)
 * @param {string} params.eventName - One of USER_ACTION_EVENTS values
 * @param {string} [params.email] - Acting user's email
 * @param {string} [params.lastActivityProfileId] - users/{id} to set lastUserActionAt (defaults to userId)
 */
export async function logUserAction({
  userId,
  eventName,
  email = "",
  lastActivityProfileId,
} = {}) {
  if (!userId || !eventName) return;

  const safeEmail = typeof email === "string" ? email.trim() : "";
  const profileId = lastActivityProfileId || userId;

  try {
    await addDoc(collection(firebase, COLLECTIONS.USER_ACTION_LOG), {
      userId,
      event_name: eventName,
      email: safeEmail,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error("[logUserAction] log write failed:", err?.message || err);
  }

  try {
    await updateDoc(doc(firebase, COLLECTIONS.USERS, profileId), {
      lastUserActionAt: serverTimestamp(),
      lastUserActionEvent: eventName,
    });
  } catch (err) {
    console.error("[logUserAction] profile update failed:", err?.message || err);
  }
}

export function logUserActionBackground(payload) {
  void logUserAction(payload);
}

/**
 * Latest log entry per user id (requires composite index: userId ASC, createdAt DESC).
 * @param {string[]} userIds
 * @returns {Promise<Record<string, { id: string; event_name: string; email: string; createdAt: unknown }>>}
 */
export async function fetchLatestUserActionLogsByUserIds(userIds = []) {
  const uniqueIds = [...new Set((userIds || []).filter(Boolean))];
  const result = {};
  const PARALLEL = 24;

  for (let i = 0; i < uniqueIds.length; i += PARALLEL) {
    const slice = uniqueIds.slice(i, i + PARALLEL);
    await Promise.all(
      slice.map(async (uid) => {
        try {
          const q = query(
            collection(firebase, COLLECTIONS.USER_ACTION_LOG),
            where("userId", "==", uid),
            orderBy("createdAt", "desc"),
            limit(1)
          );
          const snap = await getDocs(q);
          if (snap.empty) return;
          const docSnap = snap.docs[0];
          const data = docSnap.data();
          result[uid] = {
            id: docSnap.id,
            event_name: data.event_name,
            email: data.email,
            createdAt: data.createdAt,
          };
        } catch (err) {
          console.error("[fetchLatestUserActionLogsByUserIds]", uid, err?.message || err);
        }
      })
    );
  }
  return result;
}
